import { useEffect, useState } from "react";
import { BadgeCheck, Phone, Star } from "lucide-react";
import { Button, Card, cn } from "@clevis/shared-ui";
import { call } from "../../lib/callFunction";

// The two pages a customer opens from a text (no sign-in; the link's token is
// the key): "who's coming" with the tech's photo and bio, and "how did we
// do?". The Google review button is always shown, whatever the rating:
// review gating breaks Google's rules and the FTC's.

interface Tech {
  name: string;
  photoUrl: string | null;
  bio: string;
  yearsExperience: number | null;
  certifications: string[];
  backgroundChecked: boolean;
}
interface TechPageData {
  company: string;
  companyPhone: string;
  techs: Tech[];
  visit: string;
  status: "scheduled" | "on_the_way" | "arrived" | "done" | "canceled";
  eta: string;
}
interface ReviewPageData {
  company: string;
  companyPhone: string;
  techs: Tech[];
  googleReviewUrl: string;
  facebookUrl: string;
  submitted: { stars: number } | null;
}

function Shell({ company, phone, children }: { company: string; phone: string; children: React.ReactNode }) {
  return (
    <div className="min-h-full bg-cream px-4 py-6">
      <div className="mx-auto max-w-md space-y-4">
        <div className="flex items-center justify-between">
          <div className="text-lg font-bold text-ink">{company}</div>
          {phone ? (
            <a href={`tel:${phone}`} className="flex items-center gap-1 rounded-lg border border-steel/30 bg-white px-3 py-1.5 text-sm text-ink">
              <Phone size={14} /> Call
            </a>
          ) : null}
        </div>
        {children}
        <p className="text-center text-[11px] text-steel">Sent by {company} using Clevis.</p>
      </div>
    </div>
  );
}

function TechCard({ t }: { t: Tech }) {
  return (
    <Card className="flex gap-3">
      {t.photoUrl ? (
        <img src={t.photoUrl} alt={t.name} className="h-20 w-20 shrink-0 rounded-full border border-steel/20 object-cover" />
      ) : (
        <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-full bg-steel/15 text-2xl font-bold text-ink">{t.name.slice(0, 1)}</div>
      )}
      <div className="min-w-0">
        <div className="text-base font-bold text-ink">{t.name}</div>
        {t.backgroundChecked ? (
          <div className="flex items-center gap-1 text-xs text-success">
            <BadgeCheck size={13} /> Background checked
          </div>
        ) : null}
        {t.yearsExperience ? <div className="text-xs text-steel">{t.yearsExperience} years of experience</div> : null}
        {t.bio ? <p className="mt-1 text-sm text-ink">{t.bio}</p> : null}
        {t.certifications.length > 0 ? <p className="mt-1 text-xs text-steel">{t.certifications.join(" · ")}</p> : null}
      </div>
    </Card>
  );
}

const STATUS: Record<TechPageData["status"], string> = {
  scheduled: "Scheduled",
  on_the_way: "On the way",
  arrived: "Arrived, working on it",
  done: "All done",
  canceled: "This visit was canceled",
};

export function TechPage({ token }: { token: string }) {
  const [data, setData] = useState<TechPageData | null>(null);
  const [error, setError] = useState("");
  useEffect(() => {
    void call<TechPageData>("getCustomerPage", { token, kind: "tech" }, { mutation: false })
      .then(setData)
      .catch((e) => setError((e as Error).message));
  }, [token]);
  if (error) return <Shell company="" phone=""><Card><p className="text-sm text-ink">{error}</p></Card></Shell>;
  if (!data) return <Shell company="" phone=""><p className="text-sm text-steel">Loading…</p></Shell>;
  return (
    <Shell company={data.company} phone={data.companyPhone}>
      <Card>
        <div className="text-xs font-semibold uppercase tracking-wide text-steel">Your visit</div>
        <div className="text-base font-bold text-ink">{STATUS[data.status]}</div>
        {data.visit ? <div className="text-sm text-ink">{data.visit}</div> : null}
        {data.status === "on_the_way" && data.eta ? <div className="text-sm text-brandorange-dark">Arriving about {data.eta}</div> : null}
      </Card>
      <div className="text-sm font-semibold text-ink">{data.techs.length > 1 ? "Meet your technicians" : "Meet your technician"}</div>
      {data.techs.map((t, i) => (
        <TechCard key={i} t={t} />
      ))}
    </Shell>
  );
}

export function FeedbackPage({ token }: { token: string }) {
  const [data, setData] = useState<ReviewPageData | null>(null);
  const [error, setError] = useState("");
  const [stars, setStars] = useState(0);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState<number | null>(null);
  useEffect(() => {
    void call<ReviewPageData>("getCustomerPage", { token, kind: "review" }, { mutation: false })
      .then((d) => {
        setData(d);
        if (d.submitted) setSent(d.submitted.stars);
      })
      .catch((e) => setError((e as Error).message));
  }, [token]);

  async function submit() {
    if (!stars) return;
    setBusy(true);
    try {
      await call("submitCustomerFeedback", { token, stars, comment: comment.trim() }, { mutation: false });
      setSent(stars);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  const google = () => void call("submitCustomerFeedback", { token, googleClicked: true }, { mutation: false }).catch(() => undefined);

  if (error && !data) return <Shell company="" phone=""><Card><p className="text-sm text-ink">{error}</p></Card></Shell>;
  if (!data) return <Shell company="" phone=""><p className="text-sm text-steel">Loading…</p></Shell>;
  const tech = data.techs[0]?.name;
  return (
    <Shell company={data.company} phone={data.companyPhone}>
      {sent === null ? (
        <Card className="space-y-3 text-center">
          <div className="text-base font-bold text-ink">How did we do{tech ? ` with ${tech}` : ""}?</div>
          <div className="flex justify-center gap-1">
            {[1, 2, 3, 4, 5].map((n) => (
              <button key={n} aria-label={`${n} star${n > 1 ? "s" : ""}`} onClick={() => setStars(n)}>
                <Star size={36} className={n <= stars ? "text-brandorange" : "text-steel/40"} fill={n <= stars ? "currentColor" : "none"} />
              </button>
            ))}
          </div>
          {stars > 0 ? (
            <textarea
              className="w-full rounded-lg border border-steel/40 bg-white px-3 py-2 text-sm text-ink"
              rows={3}
              placeholder={stars <= 3 ? "What went wrong? We'll make it right." : "Anything you'd like to tell us? (optional)"}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
          ) : null}
          <Button className="w-full" busy={busy} disabled={!stars} onClick={() => void submit()}>
            Send
          </Button>
          {error ? <p className="text-xs text-danger">{error}</p> : null}
        </Card>
      ) : (
        <Card className="text-center">
          <div className="text-base font-bold text-ink">Thank you!</div>
          <p className="mt-1 text-sm text-ink">
            {sent <= 3 ? `We're sorry it wasn't better. ${data.company} will reach out to make it right.` : "We really appreciate it."}
          </p>
        </Card>
      )}
      {data.googleReviewUrl || data.facebookUrl ? (
        <Card className="space-y-2 text-center">
          <p className="text-sm text-ink">Would you share a review? It helps a local business a lot.</p>
          {data.googleReviewUrl ? (
            <a href={data.googleReviewUrl} target="_blank" rel="noreferrer" onClick={google} className={cn("block rounded-lg bg-ink px-4 py-2 text-sm font-semibold text-white")}>
              Review us on Google
            </a>
          ) : null}
          {data.facebookUrl ? (
            <a href={data.facebookUrl} target="_blank" rel="noreferrer" className="block rounded-lg border border-steel/30 px-4 py-2 text-sm text-ink">
              Review us on Facebook
            </a>
          ) : null}
        </Card>
      ) : null}
    </Shell>
  );
}
